import React, { useEffect, useState } from 'react';
import { makeStyles, withTheme } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Button from 'Components/UI/Button';
import TableNodeCell from './TableNodeCell';
import TableLabelCell from './TableLabelCell';

const useStyles = makeStyles(theme => ({
  table: {
    minWidth: theme.dimensions.lg,
  },
  button: {
    minWidth: theme.spacing(12),
  },
  title: {
    color: theme.palette.facet.primaryGray,
    fontWeight: theme.typography.fontWeightBold,
    padding: '16px',
  },
}));

const MultipicklistSelector = ({
  onChangePickedOptions,
  isExclusive,
  templateOptions,
  defaultOptions,
}) => {
  const classes = useStyles();
  const [pickOptions, setPickOptions] = useState([]);
  const [pickedOptions, setPickedOptions] = useState([]);
  const [pickOptionHeaders, setPickOptionHeaders] = useState([]);

  const parsePickOption = (option) => {
    const result = {
      id: option.id,
    };

    option.prompt.split('|').forEach((item) => {
      const tokens = item.split(';');
      const key = tokens[0].replace(' ', '');
      const value = tokens[1];
      result[key] = value;
    });

    return result;
  };
  const isPicked = id => pickedOptions.filter(item => item.id === id).length > 0;

  const setPickOptionSelected = (targetItem) => {
    if (isExclusive) {
      setPickedOptions([targetItem]);
    } else if (isPicked(targetItem.id)) {
      setPickedOptions(pickedOptions.filter(item => item.id !== targetItem.id));
    } else {
      setPickedOptions([...pickedOptions, targetItem]);
    }
  };

  const shouldSkipRenderingColumn = (columnName) => {
    const blacklist = ['id', '', 'ClientID'];
    return !blacklist.includes(columnName);
  };

  const parsePickOptionHeaders = (option) => {
    const tokens = option.prompt.split('|');
    const result = tokens
      .map(item => item.split(';')[0])
      .filter(item => shouldSkipRenderingColumn(item));
    return result;
  };

  const getPrettyText = (originalText) => {
    let result = originalText.split('_');
    result = result.map(word => word.charAt(0).toUpperCase() + word.slice(1));
    return result.join(' ');
  };

  useEffect(() => {
    if (templateOptions != null) {
      const optionsHeadersWrapped = templateOptions.map(option => parsePickOptionHeaders(option));
      if (optionsHeadersWrapped.length > 0) {
        setPickOptionHeaders(optionsHeadersWrapped[0]);
      }

      const parsedOptions = templateOptions.map(option => parsePickOption(option));
      setPickOptions(parsedOptions);
    }
  }, [templateOptions]);

  useEffect(() => {
    const idsOnly = pickedOptions.map(item => item.id);
    onChangePickedOptions(idsOnly);
  }, [pickedOptions]);

  useEffect(() => {
    if (defaultOptions.length > 0) {
      setPickedOptions(defaultOptions);
    }
  }, [defaultOptions]);

  return (
    <div>
      <TableContainer>
        <Table className={classes.table} size="small">
          <TableHead>
            <TableRow>
              {pickOptionHeaders.map((prompt, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <TableLabelCell isHeader key={`data-${index}`}>{prompt}</TableLabelCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {pickOptions.map(row => (
              <TableRow key={row.id}>
                {Object.keys(row)
                  .filter(column => shouldSkipRenderingColumn(column))
                  .map((column, index) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <TableLabelCell key={`cell-${index}`}>
                      {getPrettyText(row[column])}
                    </TableLabelCell>
                  ))}
                <TableNodeCell>
                  <Button
                    className={classes.button}
                    color={isPicked(row.id) ? 'secondary' : 'accentBlue'}
                    disableElevation
                    onClick={() => setPickOptionSelected(row)}
                    variant="contained"
                  >
                    {isPicked(row.id) ? 'Selected' : 'Select'}
                  </Button>
                </TableNodeCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};

MultipicklistSelector.propTypes = {
  onChangePickedOptions: PropTypes.func.isRequired,
  isExclusive: PropTypes.bool,
  templateOptions: PropTypes.array.isRequired,
  defaultOptions: PropTypes.array,
};

MultipicklistSelector.defaultProps = {
  defaultOptions: [],
  isExclusive: false,
};

export default withTheme(MultipicklistSelector);
